import React, { useEffect, useRef, useState } from 'react';
import FeaturedCardPreview from '../../featured-card-preview';
import style from './style.module.scss';

type AcvtCarouselItemProps = {
    image?: string;
    title?: string;
    subtitle?: string;
    cta?: string;
    backgroundColor?: string | null;
    titleColor?: string | null;
    subtitleColor?: string | null;
};

/**
 * Single banner inside the home page carousel. The actual card layout
 * is delegated to `<FeaturedCardPreview />` (already used by the
 * Featured/Crop edition flows) so the simulated app and the CMS edition
 * surfaces stay visually in sync.
 */
export const AcvtCarouselItem = ({
    image,
    title,
    subtitle,
    cta,
    backgroundColor,
    titleColor,
    subtitleColor,
}: AcvtCarouselItemProps) => {
    return (
        <div className={style.carousel_item}>
            <FeaturedCardPreview
                image={image}
                title={title}
                subtitle={subtitle}
                cta={cta}
                backgroundColor={backgroundColor}
                titleColor={titleColor}
                subtitleColor={subtitleColor}
            />
        </div>
    );
};

const ChevronLeft = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path d="M10.354 12.354 6 8l4.354-4.354.708.708L7.414 8l3.648 3.646-.708.708Z" />
    </svg>
);

const ChevronRight = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path d="M5.646 12.354 9.586 8 5.646 3.646l.708-.708L11 8l-4.646 5.062-.708-.708Z" />
    </svg>
);

const AcvtCarousel = (props: { children: React.ReactNode }) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const items = React.Children.toArray(props.children);
    const total = items.length;
    const [activeIndex, setActiveIndex] = useState(0);

    // Sync the active dot with whichever slide is currently snapped to
    // the center of the scroll viewport. IntersectionObserver picks the
    // child with the highest intersectionRatio inside the track.
    useEffect(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const mostVisible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort(
                        (a, b) => b.intersectionRatio - a.intersectionRatio
                    )[0];
                if (mostVisible) {
                    const idx = Number(
                        (mostVisible.target as HTMLElement).dataset.index ?? 0
                    );
                    setActiveIndex(idx);
                }
            },
            { root: track, threshold: [0.5, 0.75, 1] }
        );
        const slides = track.querySelectorAll<HTMLElement>(
            '[data-carousel-slide]'
        );
        slides.forEach((slide) => observer.observe(slide));
        return () => observer.disconnect();
    }, [total]);

    const scrollToIndex = (index: number) => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        const slide = track.querySelector<HTMLElement>(
            `[data-index="${index}"]`
        );
        if (slide) {
            slide.scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
            });
        }
    };

    const isFirst = activeIndex === 0;
    const isLast = activeIndex === total - 1;

    return (
        <div className={style.carousel}>
            <div ref={trackRef} className={style.track}>
                {items.map((child, index) => (
                    <div
                        key={index}
                        data-carousel-slide
                        data-index={index}
                        className={style.slide}
                    >
                        {child}
                    </div>
                ))}
            </div>
            <div className={style.controls}>
                <button
                    type="button"
                    className={style.chevron}
                    onClick={() => scrollToIndex(activeIndex - 1)}
                    disabled={isFirst}
                    aria-label="Previous"
                >
                    <ChevronLeft />
                </button>
                <div className={style.indicators}>
                    {items.map((_child, index) => (
                        <button
                            key={index}
                            type="button"
                            className={
                                index === activeIndex
                                    ? style.active
                                    : style.inactive
                            }
                            onClick={() => scrollToIndex(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
                <button
                    type="button"
                    className={style.chevron}
                    onClick={() => scrollToIndex(activeIndex + 1)}
                    disabled={isLast}
                    aria-label="Next"
                >
                    <ChevronRight />
                </button>
            </div>
        </div>
    );
};

export default AcvtCarousel;
